<template>
  <div>
    <v-container>
      <v-row class="justify-center">
        <v-col cols="12" sm="11" lg="11" :class="['px-0']" style="position: relative">
          <div class="declarations-sticky-header">
            <div class="header-row-1">
              <div class="search-filter-group">
                <v-text-field
                  v-model="searchTerm"
                  placeholder="Pretraži artikle"
                  dense
                  outlined
                  hide-details
                  clearable
                  class="filter-select controls-search"
                  prepend-inner-icon="mdi-magnify"
                  @input="onSearchInput"
                  @click:clear="clearSearch"
                ></v-text-field>
                <v-select
                  v-model="sortBy"
                  :items="[{ text: 'Naziv', value: 'name' }, { text: 'Najnovije prvo', value: 'created' }]"
                  outlined
                  dense
                  hide-details
                  class="filter-select controls-sort"
                  @change="changeSortBy"
                ></v-select>
                <v-autocomplete
                  v-if="locations.length > 1"
                  v-model="selectedLocationId"
                  :items="locations"
                  item-text="name"
                  item-value="id"
                  placeholder="Lokacija"
                  outlined
                  dense
                  hide-details
                  class="filter-select controls-location"
                  @change="changeLocation"
                ></v-autocomplete>
                <v-tooltip top :disabled="hasTemplate" content-class="vpc-tooltip" color="white">
                  <template v-slot:activator="{ on, attrs }">
                    <span v-on="on" v-bind="attrs">
                      <v-btn
                        icon
                        class="selection-toggle-btn"
                        :class="{ 'selection-toggle-btn--active': selectionMode }"
                        title="Printaj pojedine artikle"
                        :disabled="!hasTemplate"
                        @click="toggleSelectionMode"
                      >
                        <v-icon small>mdi-format-list-checks</v-icon>
                      </v-btn>
                    </span>
                  </template>
                  <div class="vpc-tooltip__content">Dodajte predložak klikom na "Uredi predložak"</div>
                </v-tooltip>
              </div>
              <div class="header-row-1-actions">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon x-small v-bind="attrs" v-on="on" @click="showHelp = true">
                      <v-icon size="18" color="#94A3B8">mdi-help-circle-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Prikaži upute</span>
                </v-tooltip>
                <v-btn class="edit-template-btn" elevation="0" @click="$refs.templateBuilder.open()">
                  <v-icon small class="mr-1">mdi-cog-outline</v-icon>
                  Uredi predložak
                </v-btn>
              </div>
            </div>
            <div class="header-row-2">
              <v-tooltip top :disabled="hasTemplate" content-class="vpc-tooltip" color="white">
                <template v-slot:activator="{ on, attrs }">
                  <span v-on="on" v-bind="attrs">
                    <v-btn class="add-item-btn" elevation="0" :disabled="!hasTemplate" @click="openCatalogPrintModal">
                      <v-icon small class="mr-1">mdi-book-open-page-variant-outline</v-icon>
                      {{ selectedItemIds.size > 0 ? `Printaj odabrane (${selectedItemIds.size})` : 'Knjiga deklaracija' }}
                    </v-btn>
                  </span>
                </template>
                <div class="vpc-tooltip__content">Dodajte predložak klikom na "Uredi predložak"</div>
              </v-tooltip>
              <v-tooltip top :disabled="hasTemplate" content-class="vpc-tooltip" color="white">
                <template v-slot:activator="{ on, attrs }">
                  <span v-on="on" v-bind="attrs">
                    <v-btn class="add-item-btn" elevation="0" :disabled="!hasTemplate" @click="openCatalogLabelModal">
                      <v-icon small class="mr-1">mdi-tag-outline</v-icon>
                      {{ selectedItemIds.size > 0 ? `Naljepnice odabrane (${selectedItemIds.size})` : 'Printaj naljepnice' }}
                    </v-btn>
                  </span>
                </template>
                <div class="vpc-tooltip__content">Dodajte predložak klikom na "Uredi predložak"</div>
              </v-tooltip>
            </div>

            <v-dialog v-model="showHelp" max-width="720">
              <div class="info-banner">
                <v-btn icon x-small class="info-banner__close" @click="showHelp = false">
                  <v-icon size="16">mdi-close</v-icon>
                </v-btn>
                <div class="info-banner__title">
                  <v-icon size="20" color="#0891B2" class="mr-2">mdi-file-document-outline</v-icon>
                  Upute — Deklaracije proizvoda
                </div>
                <p class="info-banner__intro">
                  Deklaracija se sastoji od polja definiranih na predlošku, koja se automatski primjenjuju na sve artikle,
                  s mogućnošću iznimke po pojedinom artiklu. Iz istih podataka generiraju se PDF/DOCX ispis, "Knjiga
                  deklaracija" (katalog svih aktivnih) i naljepnice za tiskanje na naljepnicama.
                </p>

                <div class="info-banner__section-title">1. Predložak (polja deklaracije)</div>
                <div class="info-banner__grid">
                  <div class="info-legend-item">
                    <v-icon size="16" color="#0891B2">mdi-view-column-outline</v-icon>
                    <span><strong>Uredi predložak</strong> — otvara uređivač polja koja se prikazuju na svim deklaracijama.</span>
                  </div>
                  <div class="info-legend-item">
                    <v-icon size="16" color="#0891B2">mdi-format-columns</v-icon>
                    <span><strong>Kolona 1 / Kolona 2</strong> — svako polje ide u lijevu ili desnu kolonu ispisa; ovo automatski određuje i dvokolonski raspored na naljepnicama.</span>
                  </div>
                  <div class="info-legend-item">
                    <v-icon size="16" color="#0891B2">mdi-pencil-outline</v-icon>
                    <span><strong>Ručni unos / Atribut artikla</strong> — polje se popunjava ručno (uz opcionalnu zadanu vrijednost) ili automatski povlači iz podataka artikla (naziv, šifra, barkod...).</span>
                  </div>
                  <div class="info-legend-item">
                    <v-icon size="16" color="#0891B2">mdi-checkbox-marked-outline</v-icon>
                    <span><strong>Printaj</strong> — isključivanjem se polje sprema, ali se ne prikazuje na ispisu; prazna polja se nikad ne printaju.</span>
                  </div>
                </div>

                <div class="info-banner__section-title">2. Pojedinačni artikl</div>
                <div class="info-banner__grid">
                  <div class="info-legend-item">
                    <v-icon size="16" color="#0891B2">mdi-pencil</v-icon>
                    <span><strong>Uredi</strong> — upis vrijednosti ručnih polja samo za taj artikl, ili uklanjanje/dodavanje polja koje vrijedi samo za njega.</span>
                  </div>
                  <div class="info-legend-item">
                    <v-icon size="16" color="#0891B2">mdi-printer-pos</v-icon>
                    <span><strong>Printaj na POS printer</strong> — ispis na termalni blagajnički printer, bez postavki/pretpregleda.</span>
                  </div>
                </div>

                <div class="info-banner__section-title">3. Printanje (pojedinačno, Knjiga deklaracija, odabrani)</div>
                <p class="info-banner__intro">
                  Gumb <strong>Printaj</strong> na redu, <strong>Knjiga deklaracija</strong> u zaglavlju ili odabir artikala
                  (vidi točku 5) otvaraju isti fullscreen modal s postavkama i uživo pretpregledom:
                </p>
                <div class="info-banner__grid">
                  <div class="info-legend-item">
                    <v-icon size="16" color="#0891B2">mdi-format-size</v-icon>
                    <span><strong>Veličina fonta, orijentacija, obrub</strong> — pretpregled se ažurira automatski čim promijeniš postavku.</span>
                  </div>
                  <div class="info-legend-item">
                    <v-icon size="16" color="#0891B2">mdi-file-multiple-outline</v-icon>
                    <span><strong>Broj deklaracija po stranici / Jedna po stranici</strong> — samo za Knjigu deklaracija (i odabrane), određuje koliko se deklaracija stavlja na jednu A4 stranicu.</span>
                  </div>
                  <div class="info-legend-item">
                    <v-icon size="16" color="#0891B2">mdi-printer-outline</v-icon>
                    <span><strong>Isprintaj / Preuzmi PDF / Preuzmi DOCX</strong> — postavke se spremaju tek klikom na jednu od ovih akcija.</span>
                  </div>
                  <div class="info-legend-item">
                    <v-icon size="16" color="#0891B2">mdi-domain</v-icon>
                    <span>Knjiga deklaracija na vrhu prve stranice ispisuje naziv firme — namijenjena je printanju i izlaganju u prodajnom prostoru.</span>
                  </div>
                </div>

                <div class="info-banner__section-title">4. Naljepnice</div>
                <p class="info-banner__intro">
                  Gumb <strong>Printaj naljepnice</strong> u zaglavlju, "Naljepnice" na redu ili odabir artikala otvaraju
                  zaseban modal — naljepnice imaju svoj vlastiti stil, neovisan o postavkama iz točke 3.
                </p>
                <div class="info-banner__grid">
                  <div class="info-legend-item">
                    <v-icon size="16" color="#0891B2">mdi-crop-portrait</v-icon>
                    <span><strong>Dimenzije i raspored</strong> — širina/visina kutije, broj po redu, razmaci, broj kopija (samo pojedinačni ispis) i razmak između redova teksta.</span>
                  </div>
                  <div class="info-legend-item">
                    <v-icon size="16" color="#0891B2">mdi-page-layout-sidebar-left</v-icon>
                    <span><strong>Margine stranice</strong> — gornja i lijeva margina prve naljepnice na stranici.</span>
                  </div>
                  <div class="info-legend-item">
                    <v-icon size="16" color="#0891B2">mdi-format-font</v-icon>
                    <span><strong>Dizajn i fontovi</strong> — orijentacija, veličina fonta i bold zasebno za labelu i za vrijednost, obrub oko naljepnice.</span>
                  </div>
                  <div class="info-legend-item">
                    <v-icon size="16" color="#0891B2">mdi-format-columns</v-icon>
                    <span>Ako artikl ima polja u obje kolone predloška, naljepnica ih automatski prikazuje u dvije kolone jedno pored drugog; inače koristi cijelu širinu kutije.</span>
                  </div>
                </div>

                <div class="info-banner__section-title">5. Odabir pojedinih artikala i sortiranje</div>
                <div class="info-banner__grid">
                  <div class="info-legend-item">
                    <v-icon size="16" color="#0891B2">mdi-format-list-checks</v-icon>
                    <span>Ikona pored tražilice uključuje odabir — checkboxovi na svakom redu ostaju odabrani i kad promijeniš stranicu ili pretražuješ.</span>
                  </div>
                  <div class="info-legend-item">
                    <v-icon size="16" color="#0891B2">mdi-book-open-page-variant-outline</v-icon>
                    <span>Kad je nešto odabrano, gumbi <strong>Knjiga deklaracija</strong>/<strong>Printaj naljepnice</strong> automatski printaju SAMO odabrane artikle (naziv gumba to i pokazuje).</span>
                  </div>
                  <div class="info-legend-item">
                    <v-icon size="16" color="#0891B2">mdi-sort</v-icon>
                    <span><strong>Sortiraj po</strong> — Naziv ili Najnovije prvo, korisno za brzo pronalaženje upravo dodanih artikala za odabir.</span>
                  </div>
                </div>
              </div>
            </v-dialog>
            <div class="declarations-thead">
              <div class="declarations-thead-cell" style="width:40%;">Naziv</div>
              <div class="declarations-thead-cell" style="width:20%;">Šifra</div>
              <div class="declarations-thead-cell ml-auto">Aktivno</div>
              <div class="declarations-thead-cell">Akcije</div>
            </div>
          </div>

          <div v-if="selectionMode" class="selection-toolbar">
            <span class="selection-count">Odabrano: {{ selectedItemIds.size }} {{ selectedItemIds.size === 1 ? 'artikl' : 'artikala' }}</span>
            <v-spacer></v-spacer>
            <v-btn text class="cancel-btn-eracun" @click="cancelSelectionMode">Odustani</v-btn>
          </div>

          <div v-if="!locationsLoading && locations.length === 0" class="declarations-empty-state">
            Nema definiranih prodajnih mjesta za ovu firmu — deklaracije se učitavaju s cjenika prodajnog mjesta.
          </div>
          <div v-else-if="!locationsLoading && locations.length > 1 && !selectedLocationId" class="declarations-empty-state">
            Odaberite prodajno mjesto iznad za prikaz artikala.
          </div>
          <div v-else class="declarations-rows-body">
            <v-expansion-panels focusable>
              <v-expansion-panel
                v-for="row in rows"
                :key="row.id"
                hover
                tabindex="0"
                :class="row.active ? 'status-ok-exp' : 'status-storno'"
              >
                <v-expansion-panel-header hide-actions>
                  <div class="declarations-row-inner">
                    <div v-if="selectionMode" class="declarations-cell" style="width:auto; flex-shrink:0;" @click.stop>
                      <v-checkbox :input-value="selectedItemIds.has(row.id)" color="#3989C9" hide-details dense @change="toggleSelected(row.id)"></v-checkbox>
                    </div>
                    <div class="declarations-cell" style="width:40%;">
                      <span class="item-name one-liner-text">{{ row.name }}</span>
                    </div>
                    <div class="declarations-cell" style="width:20%;">{{ row.code }}</div>
                    <div class="declarations-cell ml-auto" style="width:auto; flex-shrink:0;" @click.stop>
                      <v-switch v-model="row.active" color="#3989C9" class="declarations-switch" @change="toggleActive(row)" hide-details dense></v-switch>
                    </div>
                    <div class="declarations-cell-actions" @click.stop>
                      <v-tooltip top :disabled="hasTemplate" content-class="vpc-tooltip" color="white">
                        <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                          <span v-on="tooltipOn" v-bind="tooltipAttrs">
                            <v-menu offset-y left :disabled="!hasTemplate">
                              <template #activator="{ on, attrs }">
                                <v-btn min-height="30" min-width="25" plain rounded :disabled="!hasTemplate" v-on="on" v-bind="attrs">
                                  <v-icon>mdi-dots-vertical</v-icon>
                                </v-btn>
                              </template>
                              <v-list dense class="declarations-kebab-list">
                                <v-list-item @click="openEdit(row)">
                                  <v-icon small color="#3989C9" class="mr-2">mdi-pencil</v-icon>
                                  <v-list-item-title>Uredi</v-list-item-title>
                                </v-list-item>
                              </v-list>
                            </v-menu>
                          </span>
                        </template>
                        <div class="vpc-tooltip__content">Dodajte predložak klikom na "Uredi predložak"</div>
                      </v-tooltip>
                    </div>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div class="declaration-box">
                    <div class="declaration-box-title">{{ row.name }}</div>
                    <v-row no-gutters>
                      <v-col cols="6" v-for="col in [1, 2]" :key="col" class="declaration-box-col">
                        <div
                          v-for="field in visibleFields(row, col)"
                          :key="field.key"
                          class="expanded-field-row"
                          :class="{ 'expanded-field-row--empty': !fieldDisplayValue(row, field) }"
                        >
                          <span class="row-label">{{ field.label }}:</span>
                          <span v-if="fieldDisplayValue(row, field)" class="row-value">{{ fieldDisplayValue(row, field) }}</span>
                          <span v-else class="row-value row-value--empty">Nije definirano</span>
                        </div>
                      </v-col>
                    </v-row>
                  </div>
                  <v-row class="justify-end pr-3">
                    <v-tooltip top :disabled="hasTemplate" content-class="vpc-tooltip" color="white">
                      <template v-slot:activator="{ on, attrs }">
                        <span v-on="on" v-bind="attrs">
                          <v-btn class="flat-action-btn mr-1" text height="36" :disabled="!hasTemplate" @click="openRowPrintModal(row)">
                            <v-icon left small>mdi-printer</v-icon>Printaj
                          </v-btn>
                        </span>
                      </template>
                      <div class="vpc-tooltip__content">Dodajte predložak klikom na "Uredi predložak"</div>
                    </v-tooltip>
                    <v-tooltip top :disabled="hasTemplate" content-class="vpc-tooltip" color="white">
                      <template v-slot:activator="{ on, attrs }">
                        <span v-on="on" v-bind="attrs">
                          <v-btn class="flat-action-btn mr-1" text height="36" :disabled="!hasTemplate" @click="printRowToPos(row)">
                            <v-icon left small>mdi-printer-pos</v-icon>POS printer
                          </v-btn>
                        </span>
                      </template>
                      <div class="vpc-tooltip__content">Dodajte predložak klikom na "Uredi predložak"</div>
                    </v-tooltip>
                    <v-tooltip top :disabled="hasTemplate" content-class="vpc-tooltip" color="white">
                      <template v-slot:activator="{ on, attrs }">
                        <span v-on="on" v-bind="attrs">
                          <v-btn class="flat-action-btn" text height="36" :disabled="!hasTemplate" @click="openRowLabelModal(row)">
                            <v-icon left small>mdi-tag-outline</v-icon>Naljepnice
                          </v-btn>
                        </span>
                      </template>
                      <div class="vpc-tooltip__content">Dodajte predložak klikom na "Uredi predložak"</div>
                    </v-tooltip>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>

          <div v-if="rows.length > 0 || hasMore" class="pagination-footer">
            <v-btn
              class="page-nav-btn-header"
              :disabled="!hasPrevPage || loading"
              @click="prevPage"
              small
            >
              <v-icon small class="mr-1">mdi-chevron-left</v-icon>
              Prethodni
            </v-btn>
            <span class="pagination-info">Stranica {{ currentPage }}</span>
            <v-btn
              class="page-nav-btn-header"
              :disabled="!hasNextPage || loading"
              @click="nextPage"
              small
            >
              Sljedeći
              <v-icon small class="ml-1">mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </v-container>
    <declaration-template-builder ref="templateBuilder" @success="onTemplateSaved"></declaration-template-builder>
    <edit-declaration ref="editDeclaration" @success="loadPage" @settings-updated="onEditSettingsUpdated"></edit-declaration>
    <declaration-print-settings-modal ref="printSettingsModal" @saved="onPrintSettingsSaved"></declaration-print-settings-modal>
    <declaration-label-settings-modal ref="labelSettingsModal" @saved="onLabelSettingsSaved"></declaration-label-settings-modal>
  </div>
</template>
<script>
import { df } from '@/plugins/firebase'
import state from '@/state'
import triGram from '@/mixins/trigram'
import { getTemplate, buildDefaultFields, resolveAttributeValue, effectiveManualValue, getAllActiveDeclarationsForCatalog, getDeclarationsByIds } from './declarationsService.js'
import DeclarationTemplateBuilder from './DeclarationTemplateBuilder.vue'
import EditDeclaration from './EditDeclaration.vue'
import DeclarationPrintSettingsModal from './DeclarationPrintSettingsModal.vue'
import DeclarationLabelSettingsModal from './DeclarationLabelSettingsModal.vue'
import { printDeclarationToPos } from '@/libs/bixolon/declaration.js'

const ITEMS_PER_PAGE = 10

export default {
  name: 'Declarations',
  inject: ['showMsgBox', 'showLoader', 'hideLoader'],
  mixins: [triGram],
  components: { DeclarationTemplateBuilder, EditDeclaration, DeclarationPrintSettingsModal, DeclarationLabelSettingsModal },
  data: () => ({
    showHelp: false,
    loading: false,
    locations: [],
    locationsLoading: true,
    selectedLocationId: null,
    items: [],
    declarations: [],
    template: { fields: [] },
    searchTerm: '',
    searchTimeout: null,
    triGramObject: undefined,
    currentPage: 1,
    pageCursors: [],
    hasMore: false,
    sortBy: 'name',
    selectionMode: false,
    selectedItemIds: new Set()
  }),
  computed: {
    rows () {
      return this.items.map(item => {
        const existing = this.declarations.find(d => d.item_id === item.id)
        const declaration = existing || {
          active: true,
          fields: buildDefaultFields(this.template.fields)
        }
        return { ...item, active: declaration.active, declaration }
      })
    },
    hasPrevPage () {
      return this.currentPage > 1
    },
    hasNextPage () {
      return this.hasMore
    },
    hasTemplate () {
      return this.template.fields.length > 0
    }
  },
  beforeDestroy () {
    if (this.searchTimeout) clearTimeout(this.searchTimeout)
  },
  async mounted () {
    this.template = await getTemplate(state.getCurrentCompany().id)
    await this.loadLocations()
  },
  methods: {
    async loadLocations () {
      this.locationsLoading = true
      const companyId = state.getCurrentCompany().id
      const snapshot = await df.collection('locations').where('company_id', '==', companyId).get()
      this.locations = snapshot.docs
        .map(d => ({ ...d.data(), id: d.id }))
        .sort((a, b) => a.name.localeCompare(b.name))
      this.locationsLoading = false
      if (this.locations.length === 1) {
        this.selectedLocationId = this.locations[0].id
      }
      if (this.selectedLocationId) await this.loadPage()
    },
    changeLocation () {
      this.currentPage = 1
      this.pageCursors = []
      this.loadPage()
    },
    onSearchInput () {
      if (this.searchTimeout) clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => { this.applySearch() }, 500)
    },
    clearSearch () {
      this.searchTerm = ''
      this.applySearch()
    },
    applySearch () {
      this.triGramObject = this.searchTerm && this.searchTerm.trim().length > 0
        ? this.triGram(this.searchTerm.trim())
        : undefined
      this.currentPage = 1
      this.pageCursors = []
      this.loadPage()
    },
    visibleFields (row, column) {
      return row.declaration.fields
        .filter(f => f.column === column && !f.removed)
        .sort((a, b) => a.order - b.order)
    },
    fieldDisplayValue (row, field) {
      return field.type === 'attribute' ? resolveAttributeValue(row, field.attribute_field) : effectiveManualValue(field)
    },
    async loadPage () {
      if (!this.selectedLocationId) return
      this.loading = true
      const companyId = state.getCurrentCompany().id

      let itemsQuery = df.collection(`location_pricelist/${this.selectedLocationId}/items`).where('status', '==', 'OK')
      if (this.triGramObject) {
        Object.keys(this.triGramObject).forEach(name => {
          itemsQuery = itemsQuery.where(`meta.${name}`, '==', true)
        })
      } else if (this.sortBy === 'created') {
        itemsQuery = itemsQuery.orderBy('created', 'desc')
      } else {
        itemsQuery = itemsQuery.orderBy('name')
      }

      const cursor = this.pageCursors[this.currentPage - 1]
      if (cursor) itemsQuery = itemsQuery.startAfter(cursor)
      itemsQuery = itemsQuery.limit(ITEMS_PER_PAGE + 1)

      const itemsSnapshot = await itemsQuery.get()
      const docs = itemsSnapshot.docs
      this.hasMore = docs.length > ITEMS_PER_PAGE
      const pageDocs = docs.slice(0, ITEMS_PER_PAGE)
      this.items = pageDocs.map(d => ({ ...d.data(), id: d.id }))
      if (pageDocs.length > 0 && !this.pageCursors[this.currentPage]) {
        this.pageCursors[this.currentPage] = pageDocs[pageDocs.length - 1]
      }

      // Sparse read, scoped to this page's item ids only — items with no
      // declarations document yet are synthesized in the `rows` computed.
      const itemIds = this.items.map(item => item.id)
      this.declarations = []
      if (itemIds.length > 0) {
        const declarationsSnapshot = await df.collection('declarations')
          .where('company_id', '==', companyId)
          .where('item_id', 'in', itemIds)
          .get()
        this.declarations = declarationsSnapshot.docs.map(d => d.data())
      }

      this.loading = false
    },
    async onTemplateSaved () {
      this.template = await getTemplate(state.getCurrentCompany().id)
      await this.loadPage()
    },
    nextPage () {
      if (!this.hasNextPage) return
      this.currentPage++
      this.loadPage()
    },
    prevPage () {
      if (!this.hasPrevPage) return
      this.currentPage--
      this.loadPage()
    },
    async toggleActive (row) {
      const previousActive = !row.active
      const payload = {
        company_id: state.getCurrentCompany().id,
        item_id: row.id,
        item_name: row.name,
        item_code: row.code,
        active: row.active,
        fields: row.declaration.fields
      }
      try {
        // Always .set() the full document — this is an explicit user action, so it's
        // allowed to create the (previously nonexistent) document, unlike a plain view/close.
        await df.doc(`declarations/${row.id}`).set(payload)
        const idx = this.declarations.findIndex(d => d.item_id === row.id)
        if (idx === -1) {
          this.declarations.push(payload)
        } else {
          this.declarations.splice(idx, 1, payload)
        }
      } catch (err) {
        row.active = previousActive
        this.showMsgBox({
          text: err && err.message ? err.message : 'Greška prilikom spremanja statusa deklaracije',
          actions: ['cancel'],
          cancelBtnText: 'OK',
          color: 'error'
        })
      }
    },
    openEdit (row) {
      this.$refs.editDeclaration.open(row, row.declaration, this.template)
    },
    onEditSettingsUpdated (settings) {
      this.template.catalog_print_settings = settings
    },
    async printRowToPos (row) {
      const printerConfiguration = state.getPrinterData()
      if (!printerConfiguration || !printerConfiguration.library) {
        this.showMsgBox({
          text: 'Printer nije konfiguriran.',
          actions: ['cancel'],
          cancelBtnText: 'OK',
          color: 'error'
        })
        return
      }
      this.showLoader()
      const result = await printDeclarationToPos(row, row.declaration.fields, printerConfiguration)
      this.hideLoader()
      if (!result.success) {
        this.showMsgBox({
          text: result.error || 'Greška prilikom printanja na POS printer',
          actions: ['cancel'],
          cancelBtnText: 'OK',
          color: 'error'
        })
      }
    },
    changeSortBy () {
      this.currentPage = 1
      this.pageCursors = []
      this.loadPage()
    },
    enableSelectionMode () {
      this.selectionMode = true
      this.selectedItemIds = new Set()
    },
    cancelSelectionMode () {
      this.selectionMode = false
      this.selectedItemIds = new Set()
    },
    toggleSelectionMode () {
      if (this.selectionMode) {
        this.cancelSelectionMode()
      } else {
        this.enableSelectionMode()
      }
    },
    toggleSelected (itemId) {
      const next = new Set(this.selectedItemIds)
      if (next.has(itemId)) {
        next.delete(itemId)
      } else {
        next.add(itemId)
      }
      this.selectedItemIds = next
    },
    openRowPrintModal (row) {
      this.$refs.printSettingsModal.open('single', this.template.catalog_print_settings, { item: row, fields: row.declaration.fields })
    },
    async openCatalogPrintModal () {
      const companyId = state.getCurrentCompany().id
      const hasSelection = this.selectedItemIds.size > 0
      this.showLoader()
      const entries = hasSelection
        ? await getDeclarationsByIds(companyId, this.selectedLocationId, Array.from(this.selectedItemIds), this.template.fields)
        : await getAllActiveDeclarationsForCatalog(companyId, this.selectedLocationId, this.template.fields)
      this.hideLoader()
      if (entries.length === 0) {
        this.showMsgBox({
          text: hasSelection ? 'Odabrani artikli nisu pronađeni.' : 'Nema aktivnih deklaracija za printanje.',
          actions: ['cancel'],
          cancelBtnText: 'OK',
          color: 'error'
        })
        return
      }
      this.$refs.printSettingsModal.open(hasSelection ? 'selected' : 'catalog', this.template.catalog_print_settings, { entries })
    },
    onPrintSettingsSaved (settings) {
      this.template.catalog_print_settings = settings
    },
    openRowLabelModal (row) {
      this.$refs.labelSettingsModal.open('single', this.template.label_print_settings, { item: row, fields: row.declaration.fields })
    },
    async openCatalogLabelModal () {
      const companyId = state.getCurrentCompany().id
      const hasSelection = this.selectedItemIds.size > 0
      this.showLoader()
      const entries = hasSelection
        ? await getDeclarationsByIds(companyId, this.selectedLocationId, Array.from(this.selectedItemIds), this.template.fields)
        : await getAllActiveDeclarationsForCatalog(companyId, this.selectedLocationId, this.template.fields)
      this.hideLoader()
      if (entries.length === 0) {
        this.showMsgBox({
          text: hasSelection ? 'Odabrani artikli nisu pronađeni.' : 'Nema aktivnih deklaracija za printanje.',
          actions: ['cancel'],
          cancelBtnText: 'OK',
          color: 'error'
        })
        return
      }
      this.$refs.labelSettingsModal.open(hasSelection ? 'selected' : 'catalog', this.template.label_print_settings, { entries })
    },
    onLabelSettingsSaved (settings) {
      this.template.label_print_settings = settings
    }
  }
}
</script>
<style scoped>
/* Sticky filter header — same recipe as Lager */
.declarations-sticky-header {
  position: -webkit-sticky;
  position: sticky;
  top: 64px;
  z-index: 10;
  background: #ffffff;
  padding: 0;
  border-left: 3px solid #00BCD4;
  border-bottom: 1px solid #EEF2F5;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
}
.header-row-1 {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 16px 0;
}
.header-row-1-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}
.header-row-2 {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
  padding: 0 16px 16px;
}
.search-filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
}
.controls-search {
  width: 320px;
  flex: 0 0 auto;
}
.controls-search >>> .v-input__control,
.controls-search >>> .v-input__slot {
  height: 40px !important;
  min-height: 40px !important;
}
.controls-sort {
  width: 170px;
  flex: 0 0 auto;
}
.controls-location {
  width: 130px;
  flex: 0 0 auto;
}
.selection-toggle-btn {
  background: #ffffff !important;
  color: #00b3be !important;
  border: 1.2px solid #00b3be !important;
  border-radius: 8px !important;
  width: 40px !important;
  height: 40px !important;
  transition: background 0.15s ease, color 0.15s ease;
}
.selection-toggle-btn--active {
  background: #00BCD4 !important;
  color: #ffffff !important;
  border-color: #00BCD4 !important;
}
.edit-template-btn {
  background: transparent !important;
  color: #64748B !important;
  border: 1px solid #E2E8F0 !important;
  border-radius: 8px !important;
  height: 40px !important;
  min-height: 40px !important;
  text-transform: none !important;
  font-weight: 500 !important;
  font-size: 13px !important;
  padding: 0 16px !important;
  box-shadow: none !important;
}
.edit-template-btn:hover {
  border-color: #94A3B8 !important;
  color: #475569 !important;
}
.add-item-btn {
  background: rgba(0, 188, 212, 0.08) !important;
  color: #00838F !important;
  border: 1.2px solid #00b3be !important;
  border-radius: 8px !important;
  height: 40px !important;
  min-height: 40px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  padding: 0 16px !important;
  letter-spacing: 0.2px !important;
}

/* Table head */
.declarations-thead {
  display: flex;
  align-items: center;
  padding: 10px 8px;
  border-bottom: 1px solid #EEF2F5;
}
.declarations-thead-cell {
  font-size: 11px;
  font-weight: 600;
  color: #94A3B8;
  letter-spacing: 0.8px;
  padding: 0 8px;
}

/* Row content, matching Lager's flex-cell layout */
.declarations-row-inner {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 56px;
  padding: 0 8px;
}
.declarations-cell {
  display: flex;
  align-items: center;
  font-size: 15px;
  color: rgba(0, 0, 0, 0.87);
  padding: 0 8px;
  overflow: hidden;
  flex-shrink: 0;
}
.declarations-cell-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 8px;
  flex-shrink: 0;
}
.item-name {
  font-weight: 700;
  color: rgba(0, 0, 0, 0.87);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.declarations-switch {
  margin: 0 !important;
  padding: 0 !important;
}
.declarations-switch >>> .v-input__control,
.declarations-switch >>> .v-input--selection-controls__input {
  height: 24px !important;
  margin-top: 0 !important;
}
.declarations-switch >>> .v-messages {
  display: none !important;
}
.declarations-kebab-list .v-list-item {
  min-height: 36px;
}

/* Filter select input */
.filter-select.v-text-field--outlined >>> fieldset {
  border-color: #B0BEC5 !important;
  border-width: 1.5px !important;
  border-radius: 8px !important;
}
.filter-select.v-text-field--outlined:hover >>> fieldset {
  border-color: #00BCD4 !important;
}
.filter-select.v-input--is-focused >>> fieldset {
  border-color: #00BCD4 !important;
  border-width: 2px !important;
}
.filter-select >>> .v-input__slot {
  background: #ffffff !important;
}
.filter-select >>> .v-input__prepend-inner .v-icon {
  color: #00BCD4 !important;
}

/* Search / action button — outlined tirkizna */
.search-btn {
  background: rgba(0, 188, 212, 0.08) !important;
  color: #00838F !important;
  border: 1.2px solid #00b3be !important;
  box-shadow: none !important;
  border-radius: 8px !important;
  transition: background 0.15s ease !important;
  padding: 0 16px !important;
  height: 38px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  font-size: 13px !important;
  letter-spacing: 0.3px !important;
}
.search-btn:hover {
  background: rgba(0, 179, 190, 0.12) !important;
  box-shadow: none !important;
}
.search-btn .v-icon {
  color: #00BCD4 !important;
}

/* White outline navigation-style button */
.page-nav-btn-header {
  background: rgba(255, 255, 255, 0.95) !important;
  color: #3989c9 !important;
  border: 1px solid rgba(57, 137, 201, 0.25) !important;
  border-radius: 8px !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08) !important;
  text-transform: none !important;
  font-weight: 500 !important;
  font-size: 13px !important;
  letter-spacing: 0.3px !important;
}
.page-nav-btn-header:hover {
  background: rgba(255, 255, 255, 1) !important;
  border-color: #3989c9 !important;
  box-shadow: 0 2px 6px rgba(57, 137, 201, 0.2) !important;
}

/* Grey wrapper — creates floating card separation between rows */
.declarations-rows-body {
  background: #F5F7FA;
  padding: 6px 0 2px;
}
.declarations-empty-state {
  background: #F5F7FA;
  padding: 40px 16px;
  text-align: center;
  color: #64748B;
  font-size: 14px;
}
.declarations-rows-body .v-expansion-panels {
  background: transparent !important;
}

/* Left backbone — status-coloured thin stripe */
.v-expansion-panel {
  border: 1px solid #EEF2F5 !important;
  border-left: 3px solid #E0E0E0 !important;
  border-radius: 6px !important;
  background: #ffffff !important;
  box-shadow: none !important;
  overflow: hidden !important;
  transition: border-left-color 0.15s ease, border-color 0.15s ease, background 0.15s ease !important;
  margin-bottom: 4px !important;
}
.v-expansion-panel::before,
.v-expansion-panel::after {
  box-shadow: none !important;
  display: none !important;
}
.v-expansion-panel:not(.v-expansion-panel--active):hover {
  border-color: #00BCD4 !important;
  background: #F8FAFB !important;
}
.v-expansion-panel--active {
  border-left-color: #00BCD4 !important;
  border-color: #B2EBF2 !important;
  background: #F7FBFC !important;
}
.v-expansion-panel >>> .v-expansion-panel-header {
  padding: 0 !important;
  min-height: 56px !important;
}

/* Status-specific border colours — active/inactive declaration */
.status-ok-exp  { border-left-color: #4CAF50 !important; }
.status-storno  { border-left-color: #78909C !important; }
.status-ok-exp,
.status-storno {
  background: #ffffff !important;
}

.declaration-box {
  border: 1.5px solid #CFD8DC;
  border-radius: 10px;
  background: #ffffff;
  padding: 16px 20px;
  margin: 4px 0 12px;
}
.declaration-box-title {
  font-size: 15px;
  font-weight: 700;
  color: #263238;
  text-align: center;
  padding-bottom: 10px;
  margin-bottom: 8px;
  border-bottom: 1.5px solid #CFD8DC;
}
.declaration-box-col {
  padding: 0 16px;
}
.expanded-field-row {
  padding: 7px 4px;
}
.row-label {
  font-weight: 700;
  color: #263238;
}
.row-value {
  color: #263238;
  margin-left: 4px;
}
.row-value--empty {
  color: #94A3B8;
  font-style: italic;
}
.flat-action-btn {
  color: #64748B !important;
  background: transparent !important;
  box-shadow: none !important;
  text-transform: none !important;
  font-weight: 500 !important;
  border-radius: 8px !important;
}
.flat-action-btn:hover {
  background: rgba(0, 188, 212, 0.08) !important;
  color: #00838F !important;
}

.pagination-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 12px 0;
  border-top: 1px solid #EDF2F7;
  margin-top: 4px;
}
.pagination-info {
  font-size: 13px;
  color: #718096;
  font-weight: 500;
}

.selection-toolbar {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  background: #ffffff;
  border-bottom: 1px solid #EEF2F5;
  border-left: 3px solid #6A1B9A;
}
.selection-count {
  font-size: 13px;
  font-weight: 600;
  color: #263238;
}

.info-banner {
  position: relative;
  background: #F0F9FF;
  border-left: 4px solid #0891B2;
  border-radius: 10px;
  padding: 18px 20px 16px;
}
.info-banner__close {
  position: absolute !important;
  top: -12px;
  right: -12px;
  color: #94A3B8 !important;
  background: #fff !important;
  border: 1px solid #E2E8F0 !important;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08) !important;
}
.info-banner__close:hover {
  color: #475569 !important;
}
.info-banner__title {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 700;
  color: #2d3748;
  letter-spacing: 0.3px;
  margin-bottom: 10px;
}
.info-banner__section-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #0891B2;
  margin: 18px 0 8px;
}
.info-banner__intro {
  font-size: 13px;
  color: #334155;
  line-height: 1.6;
  margin: 0 0 12px;
  padding-right: 24px;
}
.info-banner__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 24px;
}
.info-legend-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12.5px;
  color: #64748B;
  line-height: 1.5;
}
.info-legend-item strong {
  color: #1E293B;
}
.info-legend-item .v-icon {
  flex-shrink: 0;
  margin-top: 1px;
}
@media (max-width: 600px) {
  .info-banner__grid {
    grid-template-columns: 1fr;
  }
}

.vpc-tooltip {
  background: #ffffff !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.10) !important;
  border: 1.5px solid #00BCD4 !important;
  border-radius: 10px !important;
  padding: 0 !important;
}
.vpc-tooltip__content {
  font-size: 12px;
  color: #1e293b;
  padding: 8px 10px;
  line-height: 1.6;
}
</style>
